import React, { useEffect, useState, useRef } from 'react';
import { SendIcon, RefreshCwIcon, UserIcon, BotIcon, ImageIcon, MicIcon, VideoIcon, XIcon, PaperclipIcon } from 'lucide-react';
// This would typically come from the admin console settings
const preselectedModel = {
  id: 'llama-3',
  name: 'Llama 3',
  provider: 'Meta'
};
const ChatConsole = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [mediaAttachments, setMediaAttachments] = useState([]);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages]);
  const handleSendMessage = e => {
    e.preventDefault();
    if (!input.trim() && mediaAttachments.length === 0) return;
    // Add user message with any attachments
    const userMessage = {
      role: 'user',
      content: input,
      timestamp: new Date(),
      attachments: [...mediaAttachments]
    };
    setMessages([...messages, userMessage]);
    setInput('');
    setMediaAttachments([]);
    // Simulate AI response
    setIsLoading(true);
    setTimeout(() => {
      const aiMessage = {
        role: 'assistant',
        content: `This is a simulated response from ${preselectedModel.name}. I've processed your message${mediaAttachments.length > 0 ? ' and the attached media' : ''}.`,
        timestamp: new Date(),
        model: preselectedModel
      };
      setMessages(prev => [...prev, aiMessage]);
      setIsLoading(false);
    }, 1500);
  };
  const handleNewChat = () => {
    setMessages([]);
    setMediaAttachments([]);
  };
  const handleFileUpload = e => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      // Create a URL for the file
      const fileURL = URL.createObjectURL(file);
      // Determine file type
      let type = 'other';
      if (file.type.startsWith('image/')) type = 'image';else if (file.type.startsWith('audio/')) type = 'audio';else if (file.type.startsWith('video/')) type = 'video';
      setMediaAttachments(prev => [...prev, {
        id: Date.now() + Math.random().toString(36).substr(2, 9),
        name: file.name,
        type,
        url: fileURL,
        file
      }]);
    });
    // Reset the file input
    e.target.value = null;
  };
  const handleRemoveAttachment = id => {
    setMediaAttachments(prev => prev.filter(attachment => attachment.id !== id));
  };
  const triggerFileUpload = accept => {
    if (fileInputRef.current) {
      fileInputRef.current.accept = accept;
      fileInputRef.current.click();
    }
  };
  const renderAttachmentPreview = attachment => {
    switch (attachment.type) {
      case 'image':
        return <img src={attachment.url} alt={attachment.name} className="max-h-32 rounded" data-id="element-22" />;
      case 'audio':
        return <audio controls src={attachment.url} className="max-w-full h-10" data-id="element-23" />;
      case 'video':
        return <video controls src={attachment.url} className="max-h-32 rounded" data-id="element-24" />;
      default:
        return <div className="flex items-center" data-id="element-25">
            <PaperclipIcon size={16} className="mr-1" data-id="element-26" />
            {attachment.name}
          </div>;
    }
  };
  return <div className="flex flex-col h-full" data-id="element-27">
      <div className="flex justify-between items-center mb-4" data-id="element-28">
        <h1 className="text-2xl font-bold" data-id="element-29">Chat Console</h1>
        <div className="flex items-center" data-id="element-30">
          <div className="mr-4 bg-blue-100 text-blue-800 px-3 py-1 rounded-md flex items-center" data-id="element-31">
            <BotIcon size={16} className="mr-1" data-id="element-32" />
            <span className="font-medium" data-id="element-33">
              {preselectedModel.name} ({preselectedModel.provider})
            </span>
          </div>
          <button onClick={handleNewChat} className="flex items-center text-sm px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 transition-colors" data-id="element-34">
            <RefreshCwIcon size={16} className="mr-1" data-id="element-35" />
            New Chat
          </button>
        </div>
      </div>
      <div className="flex-1 bg-white border border-gray-200 rounded-md overflow-y-auto mb-4" data-id="element-36">
        <div className="p-4 space-y-4" data-id="element-37">
          {messages.length === 0 ? <div className="text-center text-gray-500 py-10" data-id="element-38">
              <p data-id="element-39">Start a conversation with {preselectedModel.name}</p>
            </div> : messages.map((message, index) => <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-40">
                <div className={`max-w-3/4 rounded-lg p-3 ${message.role === 'user' ? 'bg-blue-100 text-blue-900' : 'bg-gray-100 text-gray-900'}`} data-id="element-41">
                  <div className="flex items-center mb-1" data-id="element-42">
                    {message.role === 'user' ? <>
                        <span className="font-semibold" data-id="element-43">You</span>
                        <UserIcon size={16} className="ml-1" data-id="element-44" />
                      </> : <>
                        <span className="font-semibold" data-id="element-45">
                          {message.model.name}
                        </span>
                        <BotIcon size={16} className="ml-1" data-id="element-46" />
                      </>}
                  </div>
                  {/* Message attachments */}
                  {message.attachments && message.attachments.length > 0 && <div className="mb-2 space-y-2" data-id="element-47">
                      {message.attachments.map(attachment => <div key={attachment.id} className="rounded bg-white bg-opacity-50 p-1" data-id="element-48">
                          {renderAttachmentPreview(attachment)}
                        </div>)}
                    </div>}
                  {/* Message text */}
                  <p className="whitespace-pre-wrap" data-id="element-49">{message.content}</p>
                  <div className="text-xs text-gray-500 mt-1" data-id="element-50">
                    {message.timestamp.toLocaleTimeString()}
                  </div>
                </div>
              </div>)}
          {isLoading && <div className="flex justify-start" data-id="element-51">
              <div className="bg-gray-100 rounded-lg p-3 text-gray-900" data-id="element-52">
                <div className="flex items-center" data-id="element-53">
                  <span className="font-semibold" data-id="element-54">{preselectedModel.name}</span>
                  <BotIcon size={16} className="ml-1" data-id="element-55" />
                </div>
                <div className="flex space-x-1 mt-2" data-id="element-56">
                  <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{
                animationDelay: '0ms'
              }} data-id="element-57"></div>
                  <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{
                animationDelay: '150ms'
              }} data-id="element-58"></div>
                  <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{
                animationDelay: '300ms'
              }} data-id="element-59"></div>
                </div>
              </div>
            </div>}
          <div ref={messagesEndRef} data-id="element-60" />
        </div>
      </div>
      {/* Media attachments preview */}
      {mediaAttachments.length > 0 && <div className="mb-3 p-2 border border-gray-200 rounded-md bg-gray-50" data-id="element-61">
          <div className="text-sm font-medium text-gray-700 mb-2" data-id="element-62">
            Attachments:
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-63">
            {mediaAttachments.map(attachment => <div key={attachment.id} className="relative group" data-id="element-64">
                <div className="bg-white p-1 rounded border border-gray-200" data-id="element-65">
                  {renderAttachmentPreview(attachment)}
                </div>
                <button onClick={() => handleRemoveAttachment(attachment.id)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-66">
                  <XIcon size={14} data-id="element-67" />
                </button>
              </div>)}
          </div>
        </div>}
      <form onSubmit={handleSendMessage} className="flex flex-col" data-id="element-68">
        {/* Media upload buttons */}
        <div className="flex mb-2 space-x-2" data-id="element-69">
          <button type="button" onClick={() => triggerFileUpload('image/*')} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors" title="Upload image" data-id="element-70">
            <ImageIcon size={20} data-id="element-71" />
          </button>
          <button type="button" onClick={() => triggerFileUpload('audio/*')} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors" title="Upload audio" data-id="element-72">
            <MicIcon size={20} data-id="element-73" />
          </button>
          <button type="button" onClick={() => triggerFileUpload('video/*')} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors" title="Upload video" data-id="element-74">
            <VideoIcon size={20} data-id="element-75" />
          </button>
          <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden" multiple data-id="element-76" />
        </div>
        {/* Message input */}
        <div className="flex items-center" data-id="element-77">
          <input type="text" value={input} onChange={e => setInput(e.target.value)} placeholder="Type your message..." className="flex-1 p-3 border border-gray-300 rounded-l-md focus:ring-blue-500 focus:border-blue-500" disabled={isLoading} data-id="element-78" />
          <button type="submit" className="bg-blue-600 text-white p-3 rounded-r-md hover:bg-blue-700 transition-colors flex items-center justify-center disabled:bg-blue-400" disabled={!input.trim() && mediaAttachments.length === 0 || isLoading} data-id="element-79">
            <SendIcon size={18} data-id="element-80" />
          </button>
        </div>
      </form>
    </div>;
};
export default ChatConsole;